import { DatePicker } from '@mui/lab';
import {
  Checkbox,
  FormControlLabel,
  Grid,
  SelectChangeEvent,
  useEventCallback,
} from '@mui/material';
import { DateTime } from 'luxon';
import { Fragment, memo } from 'react';
import RegionList from '../../province/region-list';
import ProvinceList from 'shell/components/shared/province-list';
import PmiTextField from 'shell/components/shared/text-field';
import { noop } from 'shell/utils/noop';

interface FieldProps {
  value: string | null;
  error: string;
  touched: boolean;
}

interface CheckFieldProps {
  value?: boolean;
  error: string;
  touched: boolean;
}

interface DateFieldProps {
  value: DateTime | null;
  error: string;
  touched: boolean;
}

interface FormItalianProps {
  disabled?: boolean;
  province: FieldProps;
  capital: CheckFieldProps;
  region: FieldProps;
  court: FieldProps;
  cap: FieldProps;
  fiscalCode: FieldProps;
  institutionDate: DateFieldProps;
  handleChange: (field: string, value: unknown) => void;
  isChangeName?: boolean;
}

const FormItalianComponent = ({
  disabled = false,
  province,
  capital,
  region,
  court,
  cap,
  fiscalCode,
  institutionDate,
  handleChange,
  isChangeName,
}: FormItalianProps): React.ReactElement => {
  const onChangeProvinceList = useEventCallback(
    (event: SelectChangeEvent<string>, codRegione?: string) => {
      handleChange('province', event.target.value);
      if (codRegione) {
        // valorizzare la regione in base alla provincia
        handleChange('region', codRegione);
      }
    }
  );

  return (
    <Fragment>
      <Grid item xs={12} md={3}>
        <ProvinceList
          value={province.value || ''}
          onChange={onChangeProvinceList}
          error={province.touched && Boolean(province.error)}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <FormControlLabel
          control={
            <Checkbox
              id="capital"
              name="capital"
              checked={capital.value || false}
              disabled={disabled}
              onChange={() => handleChange('capital', !capital.value)}
              inputProps={{ 'aria-label': 'Checkbox capoluogo' }}
            />
          }
          label="Capoluogo di provincia"
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <RegionList
          required
          value={region.value || ''}
          onChange={noop}
          error={region.touched && Boolean(region.error)}
          disabled
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <PmiTextField
          aria-label="Tribunale"
          label="Tribunale *"
          id="court"
          name="court"
          fullWidth
          disabled={disabled}
          type="text"
          variant="outlined"
          value={court.value}
          inputProps={{ maxLength: '6' }}
          onChange={(event) => {
            handleChange('court', event.target.value);
          }}
          error={court.touched && Boolean(court.error)}
          helperText={court.error}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <PmiTextField
          aria-label="Cap"
          label="C.A.P *"
          id="cap"
          name="cap"
          fullWidth
          inputProps={{ maxLength: '5' }}
          type="text"
          disabled={disabled}
          variant="outlined"
          value={cap.value}
          onChange={(event) => {
            handleChange('cap', event.target.value);
          }}
          error={cap.touched && Boolean(cap.error)}
          helperText={cap.error}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <PmiTextField
          aria-label="Codice fiscale"
          label="Codice fiscale *"
          id="fiscalCode"
          name="fiscalCode"
          inputProps={{ maxLength: '4' }}
          fullWidth
          type="text"
          variant="outlined"
          value={fiscalCode.value}
          onChange={(event) => {
            handleChange('fiscalCode', event.target.value);
          }}
          error={fiscalCode.touched && Boolean(fiscalCode.error)}
          helperText={fiscalCode.error}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <DatePicker
          label="Data Istituzione *"
          value={institutionDate.value || null}
          minDate={DateTime.fromISO('18000101')}
          maxDate={DateTime.now()}
          disabled={isChangeName ? false : disabled}
          onChange={(value) => handleChange('institutionDate', value)}
          renderInput={(params) => (
            <PmiTextField
              id="institutionDate"
              name="institutionDate"
              fullWidth
              {...params}
              error={institutionDate.touched && Boolean(institutionDate.error)}
              helperText={institutionDate.error}
            />
          )}
        />
      </Grid>
    </Fragment>
  );
};

FormItalianComponent.displayName = 'Form italian component';

const FormItalian = memo(FormItalianComponent);

export default FormItalian as typeof FormItalianComponent;
